import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../foundations/Icon';
import css from './Table.PaginationFooter.css';

// Pages shown on each side of the current page.
const SIBLING_PAGES = 1;
// First + last + current page + siblings + the two gap slots; with this many pages or
// fewer, collapsing into gaps saves no space, so all page buttons are shown.
const MAX_PAGES_WITHOUT_GAPS = 2 * SIBLING_PAGES + 5;

const pageItem = pageNumber => ({ type: 'page', value: pageNumber });
const gapItem = side => ({ type: 'gap', side });

// Builds the row of pagination buttons: first page, a window of pages around the current
// one, and the last page, with gap (ellipsis) markers where pages were skipped.
const buildPageList = (totalPages, currentPage) => {
    if (totalPages <= MAX_PAGES_WITHOUT_GAPS) {
        return Array.from({ length: totalPages }, (_, i) => pageItem(i + 1));
    }
    const firstPage = 1;
    const lastPage = totalPages;
    const windowStart = Math.max(firstPage + 1, currentPage - SIBLING_PAGES);
    const windowEnd = Math.min(lastPage - 1, currentPage + SIBLING_PAGES);

    const items = [pageItem(firstPage)];
    if (windowStart > firstPage + 1) items.push(gapItem('left'));
    for (let page = windowStart; page <= windowEnd; page++) items.push(pageItem(page));
    if (windowEnd < lastPage - 1) items.push(gapItem('right'));
    items.push(pageItem(lastPage));
    return items;
};

const PaginationFooter = ({ offset, limit, total, isLoading, itemsShown, onPageChange }) => {
    if (total <= limit) return null;
    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const first = offset + 1;
    const last = Math.min(offset + (itemsShown ?? Math.min(limit, total - offset)), total);
    const prevDisabled = offset === 0 || isLoading;
    const nextDisabled = currentPage >= totalPages || isLoading;
    const goToPage = page => onPageChange((page - 1) * limit);

    return (
        <div className={css.container}>
            <span className={css.itemsCount}>
                <Translate id="STATIC.PAGINATION.SHOWING_RANGE" data={{ first, last, total }} />
            </span>
            <div className={css.controls} role="navigation" aria-label="Pagination">
                <button
                    type="button"
                    className={classNames(css.navBtn, { [css.disabled]: prevDisabled })}
                    disabled={prevDisabled}
                    onClick={() => goToPage(currentPage - 1)}
                >
                    <Icon
                        name="chevron-left"
                        size={IconSizes.SMALL}
                        variant={prevDisabled ? IconVariants.LIGHT : IconVariants.DARK}
                        interactive={false}
                    />
                    <Translate id="STATIC.PAGINATION.PREV" />
                </button>
                {buildPageList(totalPages, currentPage).map(item =>
                    item.type === 'gap' ? (
                        <span key={`gap_${item.side}`} className={css.ellipsis} aria-hidden="true">
                            <Icon name="dots" size={IconSizes.SMALL} variant={IconVariants.LIGHT} interactive={false} />
                        </span>
                    ) : (
                        <button
                            key={item.value}
                            type="button"
                            className={classNames(css.pageBtn, { [css.pageBtnActive]: item.value === currentPage })}
                            disabled={isLoading}
                            aria-label={`Page ${item.value}`}
                            aria-current={item.value === currentPage ? 'page' : undefined}
                            onClick={() => goToPage(item.value)}
                        >
                            {item.value}
                        </button>
                    )
                )}
                <button
                    type="button"
                    className={classNames(css.navBtn, { [css.disabled]: nextDisabled })}
                    disabled={nextDisabled}
                    onClick={() => goToPage(currentPage + 1)}
                >
                    <Translate id="STATIC.PAGINATION.NEXT" />
                    <Icon
                        name="chevron-right"
                        size={IconSizes.SMALL}
                        variant={nextDisabled ? IconVariants.LIGHT : IconVariants.DARK}
                        interactive={false}
                    />
                </button>
            </div>
        </div>
    );
};

PaginationFooter.propTypes = {
    offset: PropTypes.number.isRequired,
    limit: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    isLoading: PropTypes.bool,
    itemsShown: PropTypes.number,
    onPageChange: PropTypes.func.isRequired,
};

export default PaginationFooter;
